import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import SiteFooter from "../components/layout/SiteFooter";
import SiteNavbar from "../components/layout/SiteNavbar";
import GeneralLineIcon from "../components/common/GeneralLineIcon";
import useRevealOnScroll from "../components/common/useRevealOnScroll";
import fispLogo from "../assets/logo-iniciales-oscuro.png";
import { cursos } from "../../educativo/assets/InfoCursos";
import { routes } from "../routes/newsApi";
import { fetchHomeNews, normalizeCategoryLabel, resolveMediaUrl } from "../../fisp-tv/data/noticias";

const newsVisualPresets = [
    { accent: "var(--red)", gradient: "linear-gradient(135deg,#2d1a1a,#4a1e2e)" },
    { accent: "#2563EB", gradient: "linear-gradient(135deg,#1a2040,#2a3a6a)" },
    { accent: "#D97706", gradient: "linear-gradient(135deg,#2a1f0a,#5a4010)" },
    { accent: "#7C3AED", gradient: "linear-gradient(135deg,#1f1a30,#3a2a5a)" },
    { accent: "#059669", gradient: "linear-gradient(135deg,#0a2a1a,#1a4a2a)" },
    { accent: "var(--red-light)", gradient: "linear-gradient(135deg,#2d1520,#4a2030)" },
    { accent: "#0EA5E9", gradient: "linear-gradient(135deg,#0f1b33,#21406b)" },
];

const fallbackHomeNews = Array.from({ length: 6 }, (_, index) => ({
    id: `fallback-${index + 1}` ,
    category: "FISP TV",
    title: index === 0 ? "Contenido FISP TV" : "Próximamente",
    description: "Estamos cargando las noticias más recientes.",
    href: "/fisp-tv",
    external: false,
    image: "",
    accent: newsVisualPresets[index % newsVisualPresets.length].accent,
    gradient: newsVisualPresets[index % newsVisualPresets.length].gradient,
    meta: "Actualización",
}));

function formatNewsTitle(value) {
    const raw = (value || "").trim();

    if (!raw) {
        return raw;
    }

    if (raw.includes("|")) {
        const [prefix, ...rest] = raw.split("|");

        return `${normalizeCategoryLabel(prefix)} | ${rest.join("|").trim()}`;
    }

    if (/^#?[\p{L}\d_-]+$/u.test(raw) && /[_-]/.test(raw)) {
        return normalizeCategoryLabel(raw);
    }

    return raw;
}

function normalizeHomeNewsItem(item, index, server) {
    const visual = newsVisualPresets[index % newsVisualPresets.length];

    return {
        id: item.id || `news-${index}` ,
        category: normalizeCategoryLabel(item.category || item.categoria || "FISP TV"),
        title: formatNewsTitle(item.title || item.titulo || "Contenido FISP"),
        description: (item.description || item.descripcion || "Último contenido de FISP.").trim(),
        href: item.href || "/fisp-tv",
        external: Boolean(item.external),
        image: item.image || resolveMediaUrl(item.imagen, server),
        accent: visual.accent,
        gradient: visual.gradient,
        meta: item.embedUrl || (item.link_video || "").trim() ? "Video" : "Última publicación",
    };
}

function getNewsThumbStyle(item) {
    if (item.image) {
        return {
            backgroundImage: `linear-gradient(135deg, #0f1b3328, #1b2a4a55), url("${item.image}")`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
        };
    }

    return {
        background: item.gradient,
    };
}

function getProgramLink(program) {
    return `/${program.routeFamily}/${program.routeSlug}`;
}

function getProgramTitle(program) {
    return (program.nombre || "")
        .replace(/^Posgrado en\s+/i, "")
        .replace(/^Diplomatura en\s+/i, "")
        .replace(/^Curso de\s+/i, "");
}

function getProgramDescription(program) {
    return (program.descripcionPreview || program.descripcion || "").trim();
}

function getProgramMeta(program) {
    if (program.duracion) {
        return `${program.duracion} ${program.duracion === 1 ? "mes" : "meses"}`;
    }

    if (program.clases) {
        return `${program.clases} ${program.clases === 1 ? "clase" : "clases"}`;
    }

    if (program.modalidad) {
        return program.modalidad;
    }

    return "Inscripciones abiertas";
}

const featuredProgramsByType = {
    posgrados: cursos.filter((program) => program.tipo === "Posgrado").slice(0, 2),
    diplomaturas: cursos.filter((program) => program.tipo === "Diplomatura").slice(0, 2),
    cursos: cursos.filter((program) => program.tipo === "Curso").slice(0, 2),
};

const featuredPrograms = [
    featuredProgramsByType.posgrados[0],
    featuredProgramsByType.diplomaturas[0],
    featuredProgramsByType.cursos[0],
    featuredProgramsByType.posgrados[1],
    featuredProgramsByType.diplomaturas[1],
    featuredProgramsByType.cursos[1],
].filter(Boolean).map((program) => ({
    ...program,
    cardLink: getProgramLink(program),
    cardTitle: getProgramTitle(program),
    cardDescription: getProgramDescription(program),
    cardMeta: getProgramMeta(program),
    cardType: (program.tipo || "").toLowerCase(),
}));

const serviceCards = [
    {
        icon: "wellness",
        title: "Wellness Corporativo",
        description: "Programas de bienestar para empresas: charlas sobre salud mental, nutrición, manejo del estrés y hábitos saludables para equipos de trabajo.",
        to: "/servicios/wellness-corporativo",
    },
    {
        icon: "healthTech",
        title: "Proyectos Tecnológicos en Salud",
        description: "Asesoramiento en telemedicina, implementación de tecnología sanitaria y desarrollo de proyectos tecnológicos para instituciones de salud.",
        to: "/servicios/proyectos-tecnologicos",
    },
    {
        icon: "marketing",
        title: "Marketing y Publicidad en Salud",
        description: "Estrategias de posicionamiento, campañas publicitarias y branding para empresas, productos y profesionales del sector salud.",
        to: "/servicios/marketing-publicidad",
    },
    {
        icon: "medical",
        title: "Servicios Médicos",
        description: "Relevamiento de áreas cardioprotegidas, capacitación en moto médicas y cursos de RCP virtual y presencial.",
        to: "/servicios/servicios-medicos",
    },
    {
        icon: "production",
        title: "Producción de Contenido y Cobertura de Eventos",
        description: "Producción audiovisual profesional, cobertura de congresos, jornadas y eventos de salud con difusión en FISP TV y redes.",
        to: "/servicios/produccion-eventos",
    },
    {
        icon: "training",
        title: "Capacitación Laboral para la Industria de la Salud",
        description: "Formación a medida para equipos de salud: cursos in-company, talleres técnicos y programas de actualización profesional.",
        to: "/servicios/capacitacion-laboral",
    },
];

const interestProgramGroups = ["Posgrado", "Diplomatura", "Curso"]
    .map((type) => ({
        label: `${type}s`,
        options: cursos
            .filter((program) => program.tipo === type && !program.cuposLlenos && program.nombre)
            .map((program) => program.nombre.trim())
            .sort((left, right) => left.localeCompare(right, "es")),
    }))
    .filter((group) => group.options.length > 0);

export default function Home() {
    const pageRef = useRef(null);
    const [server, setServer] = useState(routes.Vercel);
    const [latestNews, setLatestNews] = useState(fallbackHomeNews);

    useRevealOnScroll(pageRef);

    useEffect(() => {
        let active = true;

        fetch(`${routes.Hostinger}/ping`)
            .then(async (response) => {
                const data = await response.text();

                if (!active) {
                    return;
                }

                if (data === "pong") {
                    setServer(routes.Hostinger);
                } else {
                    setServer(routes.Vercel);
                }
            })
            .catch(() => {
                if (active) {
                    setServer(routes.Vercel);
                }
            });

        return () => {
            active = false;
        };
    }, []);

    useEffect(() => {
        const controller = new AbortController();

        async function loadLatestNews() {
            try {
                const homeNews = await fetchHomeNews(controller.signal, server);

                if (!homeNews.length) {
                    return;
                }

                const items = homeNews.slice(0, 6).map((item, index) => normalizeHomeNewsItem(item, index, server));

                if (items.length) {
                    setLatestNews(items);
                }
            } catch (error) {
                if (error.name !== "AbortError") {
                    console.error("Error cargando noticias del home:", error);
                }
            }
        }

        loadLatestNews();

        return () => controller.abort();
    }, [server]);

    const featuredNews = latestNews[0] || fallbackHomeNews[0];
    const newsList = Array.from({ length: 5 }, (_, index) => latestNews[index + 1] || fallbackHomeNews[index + 1]);

    return (
        <div className="home" ref={pageRef}>
            <SiteNavbar />

            <section className="hero">
                <div className="hero-img"></div>
                <div className="hero-overlay"></div>
                <div className="hero-inner">
                    <div className="hero-content">
                        <h1>
                            Salud que
                            <span> transforma.</span>
                        </h1>
                        <p className="hero-lead">Hay fundaciones que hablan de salud. FISP la construye.</p>
                        <p className="hero-desc">
                            Somos el ecosistema de salud que transforma a través de la educación y el multimedio líder del sector. Informamos, construimos bienestar en las empresas y en cada contenido que producimos. Unimos formación, comunicación y acción para que la salud llegue más lejos.
                        </p>
                        <div className="hero-btns">
                            <Link to="/educativo" className="btn-w hero-btn-primary">
                                Oferta educativa
                            </Link>
                            <Link to="/fisp-tv" className="btn-w-ghost hero-btn-secondary">
                                Ver FISP TV
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
                                    <path d="M5 12h14M12 5l7 7-7 7"></path>
                                </svg>
                            </Link>
                        </div>
                    </div>
                    <div className="hero-logo-mark" aria-hidden="true">
                        <img className="hero-logo-mark-base" src={fispLogo} alt="" />
                        <span className="hero-logo-mark-icon-shell">
                            <img className="hero-logo-mark-icon" src={fispLogo} alt="" />
                        </span>
                    </div>
                </div>
            </section>

            <div className="stats">
                <div className="stats-inner">
                    <div className="stat reveal">
                        <div className="stat-n">350K</div>
                        <div className="stat-l">Seguidores en redes</div>
                    </div>
                    <div className="stat reveal">
                        <div className="stat-n">1.500+</div>
                        <div className="stat-l">Alumnos anualmente</div>
                    </div>
                    <div className="stat reveal">
                        <div className="stat-n">FISP | UBA</div>
                        <div className="stat-l">Doble titulación</div>
                    </div>
                    <div className="stat reveal">
                        <div className="stat-n">12+</div>
                        <div className="stat-l">Países con alumnos</div>
                    </div>
                </div>
            </div>

            <section className="section edu-section">
                <div className="sec-inner">
                    <div className="sec-head">
                        <span className="sec-tag edu">Educativo</span>
                        <h2 className="sec-title">Posgrados, diplomaturas y cursos en salud</h2>
                        <p className="sec-sub">
                            Formación profesional con los más altos estándares académicos y tecnológicos. Título de FISP y Facultad de Medicina de la Universidad de Buenos
                            Aires.
                        </p>
                    </div>
                    <div className="edu-grid">
                        {featuredPrograms.map((program) => (
                            <Link key={program.id} to={program.cardLink} className="c-card" data-type={program.cardType}>
                                <div className="c-card-top">
                                    {program.foto ? <img className="c-card-cover" src={program.foto} alt={program.cardTitle} loading="lazy" /> : <div className="bg g1"></div>}
                                </div>
                                <div className="c-card-body">
                                    <span className="type">{program.tipo}</span>
                                    <h3>{program.cardTitle}</h3>
                                    <p>{program.cardDescription}</p>
                                    <div className="c-meta">
                                        <span>
                                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <circle cx="12" cy="12" r="10"></circle>
                                                <path d="M12 6v6l4 2"></path>
                                            </svg>
                                            {program.cardMeta}
                                        </span>
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                    <Link to="/educativo" className="see-all">
                        Ver toda la oferta educativa
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                            <path d="M5 12h14M12 5l7 7-7 7"></path>
                        </svg>
                    </Link>
                </div>
            </section>

            <section className="section">
                <div className="sec-inner">
                    <div className="sec-head center reveal">
                        <span className="sec-tag edu">Tu camino en FISP</span>
                        <h2 className="sec-title">Cómo inscribirse</h2>
                        <p className="sec-sub">Desde la inscripción hasta tu certificado con doble titulación FISP | UBA.</p>
                    </div>
                    <div className="steps reveal">
                        <div className="step">
                            <div className="step-n">1</div>
                            <h3>Elegí tu formación</h3>
                            <p>Posgrados, diplomaturas o cursos en las áreas de salud que necesitás.</p>
                        </div>
                        <div className="step">
                            <div className="step-n">2</div>
                            <h3>Inscribite online</h3>
                            <p>Completá el formulario de inscripción.</p>
                        </div>
                        <div className="step">
                            <div className="step-n">3</div>
                            <h3>Cursá en el campus</h3>
                            <p>Clases en vivo y asincrónicas en nuestro campus. Modalidad online y prácticas.</p>
                        </div>
                        <div className="step">
                            <div className="step-n">4</div>
                            <h3>Recibí tu título</h3>
                            <p>Doble título de FISP y de la Facultad de Medicina de la Universidad de Buenos Aires.</p>
                        </div>
                    </div>
                </div>
            </section>

            <section className="section news-section">
                <div className="sec-inner" style={{ position: "relative", zIndex: "2" }}>
                    <div className="sec-head reveal">
                        <span className="sec-tag">FISP TV</span>
                        <h2 className="sec-title">La nueva era de la comunicación en salud</h2>
                        <p className="sec-sub">Informes, entrevistas, podcasts y vlogs dedicados a la salud y el bienestar.</p>
                    </div>
                    <div className="news-grid reveal">
                        <a
                            className="n-feat"
                            href={featuredNews.href}
                            target={featuredNews.external ? "_blank" : undefined}
                            rel={featuredNews.external ? "noreferrer" : undefined}
                        >
                            <div className="n-feat-thumb" style={getNewsThumbStyle(featuredNews)}>
                                <div className="play">
                                    <svg viewBox="0 0 24 24" fill="currentColor">
                                        <polygon points="5 3 19 12 5 21"></polygon>
                                    </svg>
                                </div>
                            </div>
                            <div className="n-feat-body">
                                <span className="tag-sm">
                                    {featuredNews.category} · {featuredNews.meta}
                                </span>
                                <h3>{featuredNews.title}</h3>
                                <p>{featuredNews.description}</p>
                            </div>
                        </a>
                        <div className="n-list">
                            {newsList.map((item) => (
                                <a
                                    key={item.id}
                                    href={item.href}
                                    className="n-item"
                                    target={item.external ? "_blank" : undefined}
                                    rel={item.external ? "noreferrer" : undefined}
                                >
                                    <div className="n-thumb" style={getNewsThumbStyle(item)}>
                                        <span className="play-sm">
                                            <svg viewBox="0 0 24 24" fill="currentColor">
                                                <polygon points="5 3 19 12 5 21"></polygon>
                                            </svg>
                                        </span>
                                    </div>
                                    <span className="bar" style={{ background: item.accent }}></span>
                                    <div className="n-body">
                                        <h4>{item.title}</h4>
                                        <p>{item.category}</p>
                                    </div>
                                </a>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            <section className="section services-section">
                <div className="sec-inner">
                    <div className="sec-head reveal">
                        <span className="sec-tag srv">Servicios empresariales</span>
                        <h2 className="sec-title">Soluciones para empresas e instituciones de salud</h2>
                        <p className="sec-sub">Servicios empresariales para potenciar tu marca, asesoramiento sobre productos de salud y proyectos tecnológicos sanitarios.</p>
                    </div>
                    <div className="srv-grid reveal">
                        {serviceCards.map((service) => (
                            <Link key={service.to} to={service.to} className="srv-card">
                                <div className="srv-ico">
                                    <GeneralLineIcon name={service.icon} />
                                </div>
                                <h3>{service.title}</h3>
                                <p>{service.description}</p>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            <section className="section test-section">
                <div className="sec-inner">
                    <div className="sec-head center reveal">
                        <span className="sec-tag edu">Testimonios</span>
                        <h2 className="sec-title">Lo que dicen nuestros alumnos</h2>
                    </div>
                    <div className="test-grid reveal">
                        <div className="test-card">
                            <div className="test-q">"</div>
                            <p className="test-txt">
                                La diplomatura en TCA me dio herramientas concretas para el abordaje interdisciplinario. Excelente nivel de docentes y casos reales.
                            </p>
                            <div className="test-auth">
                                <div className="test-av">ML</div>
                                <div>
                                    <h4>Lic. Mariana López</h4>
                                    <p>Nutricionista · Buenos Aires</p>
                                </div>
                            </div>
                        </div>
                        <div className="test-card">
                            <div className="test-q">"</div>
                            <p className="test-txt">
                                Cursé la Diplomatura en Skincare. La plataforma virtual es muy buena y los contenidos están súper actualizados.
                            </p>
                            <div className="test-auth">
                                <div className="test-av">CR</div>
                                <div>
                                    <h4>Dra. Carolina Restrepo</h4>
                                    <p>Cosmiatra · Bogotá, Colombia</p>
                                </div>
                            </div>
                        </div>
                        <div className="test-card">
                            <div className="test-q">"</div>
                            <p className="test-txt">El título con respaldo UBA le dio otro peso a mi CV. Los módulos de IA Médica son de lo mejor que vi en formación online.</p>
                            <div className="test-auth">
                                <div className="test-av">FP</div>
                                <div>
                                    <h4>Dr. Federico Pérez</h4>
                                    <p>Médico clínico · CABA</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section className="cta-section">
                <div className="cta-mesh"></div>
                <div className="cta-inner">
                    <div className="cta-content reveal">
                        <h2>Empezá tu formación en salud hoy</h2>
                        <p>Completá el formulario y recibí información sobre posgrados, diplomaturas y cursos con doble titulación FISP | UBA.</p>
                        <div className="cta-checks">
                            <span className="cta-chk">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                                    <path d="m9 11 3 3L22 4"></path>
                                </svg>
                                100% Online
                            </span>
                            <span className="cta-chk">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                                    <path d="m9 11 3 3L22 4"></path>
                                </svg>
                                Título FISP | UBA
                            </span>
                            <span className="cta-chk">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                                    <path d="m9 11 3 3L22 4"></path>
                                </svg>
                                WhatsApp directo
                            </span>
                        </div>
                    </div>
                    <div className="cta-form reveal">
                        <h3>Solicitá información</h3>
                        <div className="fg">
                            <label>Nombre completo</label>
                            <input type="text" placeholder="Ej: María González" />
                        </div>
                        <div className="fg">
                            <label>Email</label>
                            <input type="email" placeholder="tu@email.com" />
                        </div>
                        <div className="fg">
                            <label>Formación de interés</label>
                            <select>
                                <option>Seleccionar formación</option>
                                {interestProgramGroups.map((group) => (
                                    <optgroup key={group.label} label={group.label}>
                                        {group.options.map((option) => (
                                            <option key={option}>{option}</option>
                                        ))}
                                    </optgroup>
                                ))}
                                <option>Otra</option>
                            </select>
                        </div>
                        <button className="btn-wh">
                            Quiero más información
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                <path d="M5 12h14M12 5l7 7-7 7"></path>
                            </svg>
                        </button>
                    </div>
                </div>
            </section>

            <SiteFooter />
        </div>
    );
}

